// src/components/Balance.jsx
import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const rupiah = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

const formatDate = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("id-ID", {
    year: "numeric", month: "short", day: "numeric",
  });
};

export default function Balance({ api }) {
  const [members, setMembers] = useState([]);
  const [memberId, setMemberId] = useState("");
  const [cutoff, setCutoff] = useState(() => new Date().toISOString().slice(0, 10));
  const [sisaBunga, setSisaBunga] = useState("");

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Ambil daftar anggota untuk dropdown
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/members", { params: { per_page: 500 } });
        const list = res.data?.data || res.data || [];
        setMembers(list);
      } catch (e) {
        console.error("Error fetching members:", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hitung = async () => {
    if (!memberId) {
      setError("Pilih anggota dulu.");
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const params = { member_id: memberId, cutoff };
      if (sisaBunga !== "") params.sisa_bunga = sisaBunga;
      const res = await api.get("/balance", { params });
      setResult(res.data);
      // Isi otomatis field sisa bunga dengan estimasi kalau masih kosong
      if (sisaBunga === "" && res.data?.debet?.est_sisa_bunga != null) {
        setSisaBunga(String(res.data.debet.est_sisa_bunga));
      }
    } catch (e) {
      console.error("Error fetching balance:", e);
      setError("Gagal menghitung balance. Pastikan backend berjalan & token valid.");
    } finally {
      setLoading(false);
    }
  };

  // ─── Export Excel ──────────────────────────────────────────────────────────
  const exportExcel = () => {
    if (!result) return;
    const m = result.member;
    const k = result.kredit;
    const d = result.debet;

    const rows = [
      ["HUTANG PIUTANG KARYAWAN - KopKar Crown Worldwide Indonesia"],
      [],
      ["Nama", m.name, "", "Tanggal Masuk", m.join_date || "-"],
      ["No Anggota", m.member_no, "", "Kepesertaan", m.kepesertaan_label],
      ["Per tanggal", result.cutoff],
      [],
      ["KREDIT (A)", "", "DEBET (B)", ""],
      ["Simpanan Pokok (SP)", k.simpanan_pokok, "Sisa Pokok Pinjaman", d.sisa_pokok],
      ["Simpanan Wajib (SW)", k.simpanan_wajib, "Sisa Bunga Pinjaman", d.sisa_bunga],
      ["Simpanan Sukarela (SS)", k.simpanan_sukarela, "", ""],
      ["Tabungan Lain-lain", k.tabungan_lain, "", ""],
      [`Jasa Kepesertaan (${m.kepesertaan_label})`, k.jasa_kepesertaan, "", ""],
      ["Pembebasan Bunga Pinjaman", k.pembebasan_bunga, "", ""],
      ["Total (A)", k.total, "Total (B)", d.total],
      [],
      ["Selisih (A-B)", result.selisih],
      ["Pembulatan", result.pembulatan],
      ["Catatan", result.payout_direction, result.pembulatan],
      ["Terbilang", result.terbilang],
    ];

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Balance");
    XLSX.writeFile(wb, `Balance_${m.name.replace(/\s+/g, "_")}_${result.cutoff}.xlsx`);
  };

  // ─── Export PDF ────────────────────────────────────────────────────────────
  const exportPDF = () => {
    if (!result) return;
    const m = result.member;
    const k = result.kredit;
    const d = result.debet;

    const doc = new jsPDF();
    doc.setFontSize(13);
    doc.text("Hutang Piutang Karyawan - KopKar Crown Worldwide Indonesia", 14, 15);
    doc.setFontSize(10);
    doc.text(`Nama: ${m.name}`, 14, 24);
    doc.text(`No Anggota: ${m.member_no}`, 14, 30);
    doc.text(`Tanggal Masuk: ${formatDate(m.join_date)}`, 120, 24);
    doc.text(`Kepesertaan: ${m.kepesertaan_label}`, 120, 30);
    doc.text(`Per tanggal: ${formatDate(result.cutoff)}`, 14, 36);

    autoTable(doc, {
      startY: 42,
      head: [["KREDIT (A)", "Jumlah", "DEBET (B)", "Jumlah"]],
      body: [
        ["Simpanan Pokok (SP)", rupiah(k.simpanan_pokok), "Sisa Pokok Pinjaman", rupiah(d.sisa_pokok)],
        ["Simpanan Wajib (SW)", rupiah(k.simpanan_wajib), "Sisa Bunga Pinjaman", rupiah(d.sisa_bunga)],
        ["Simpanan Sukarela (SS)", rupiah(k.simpanan_sukarela), "", ""],
        ["Tabungan Lain-lain", rupiah(k.tabungan_lain), "", ""],
        [`Jasa Kepesertaan`, rupiah(k.jasa_kepesertaan), "", ""],
        ["Pembebasan Bunga", rupiah(k.pembebasan_bunga), "", ""],
      ],
      foot: [["Total (A)", rupiah(k.total), "Total (B)", rupiah(d.total)]],
      styles: { fontSize: 9 },
      headStyles: { fillColor: [59, 130, 246] },
      footStyles: { fillColor: [226, 232, 240], textColor: 20, fontStyle: "bold" },
    });

    let y = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(11);
    doc.text(`Selisih (A-B): ${result.formatted.pembulatan}`, 14, y);
    doc.setFontSize(10);
    doc.text(`${result.payout_direction}: ${result.formatted.pembulatan}`, 14, y + 7);
    doc.text(`Terbilang: ${result.terbilang}`, 14, y + 14);

    doc.save(`Balance_${m.name.replace(/\s+/g, "_")}_${result.cutoff}.pdf`);
  };

  const Row = ({ label, value, strong }) => (
    <div className="flex justify-between py-1.5 text-sm">
      <span className={strong ? "font-semibold text-slate-800" : "text-slate-600"}>{label}</span>
      <span className={strong ? "font-semibold text-slate-800" : "text-slate-700"}>{rupiah(value)}</span>
    </div>
  );

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-bold text-slate-800">Balance Tabungan (Anggota Keluar)</h1>

      {/* Form pilih anggota */}
      <div className="rounded-2xl border bg-white p-5">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="lg:col-span-2">
            <label className="text-xs text-slate-500">Anggota</label>
            <select
              value={memberId}
              onChange={(e) => setMemberId(e.target.value)}
              className="w-full border rounded-xl px-3 py-2 text-sm mt-1"
            >
              <option value="">-- Pilih anggota --</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.member_no} - {m.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Per Tanggal</label>
            <input
              type="date"
              value={cutoff}
              onChange={(e) => setCutoff(e.target.value)}
              className="w-full border rounded-xl px-3 py-2 text-sm mt-1"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Sisa Bunga (opsional)</label>
            <input
              type="number"
              value={sisaBunga}
              onChange={(e) => setSisaBunga(e.target.value)}
              placeholder="otomatis estimasi"
              className="w-full border rounded-xl px-3 py-2 text-sm mt-1"
            />
          </div>
        </div>
        <button
          onClick={hitung}
          className="mt-4 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-700"
        >
          Hitung Balance
        </button>
      </div>

      {error && (
        <div className="rounded-2xl bg-red-50 border border-red-200 p-4 text-red-700 text-sm">
          {error}
        </div>
      )}

      {loading && <div className="text-slate-400 text-sm">Memuat data...</div>}

      {result && !loading && (
        <>
          {/* Info anggota */}
          <div className="rounded-2xl bg-white border p-5">
            <div className="grid sm:grid-cols-3 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-500">Nama</p>
                <p className="font-medium">{result.member.name}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Tanggal Masuk</p>
                <p className="font-medium">{formatDate(result.member.join_date)}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Kepesertaan</p>
                <p className="font-medium">{result.member.kepesertaan_label}</p>
              </div>
            </div>
          </div>

          {/* Neraca KREDIT vs DEBET */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl border bg-white p-5">
              <h3 className="font-semibold text-slate-700 mb-2 border-b pb-2">KREDIT (A)</h3>
              <Row label="Simpanan Pokok (SP)" value={result.kredit.simpanan_pokok} />
              <Row label="Simpanan Wajib (SW)" value={result.kredit.simpanan_wajib} />
              <Row label="Simpanan Sukarela (SS)" value={result.kredit.simpanan_sukarela} />
              <Row label="Tabungan Lain-lain" value={result.kredit.tabungan_lain} />
              <Row label={`Jasa Kepesertaan (${result.member.kepesertaan_label})`} value={result.kredit.jasa_kepesertaan} />
              <Row label="Pembebasan Bunga" value={result.kredit.pembebasan_bunga} />
              <div className="border-t mt-2 pt-2">
                <Row label="Total (A)" value={result.kredit.total} strong />
              </div>
            </div>

            <div className="rounded-2xl border bg-white p-5">
              <h3 className="font-semibold text-slate-700 mb-2 border-b pb-2">DEBET (B)</h3>
              <Row label="Sisa Pokok Pinjaman" value={result.debet.sisa_pokok} />
              <Row label="Sisa Bunga Pinjaman" value={result.debet.sisa_bunga} />
              <div className="border-t mt-2 pt-2" style={{ marginTop: "auto" }}>
                <Row label="Total (B)" value={result.debet.total} strong />
              </div>
            </div>
          </div>

          {/* Hasil selisih */}
          <div className="rounded-2xl shadow-sm p-5 bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <p className="text-sm opacity-90">{result.payout_direction}</p>
            <p className="text-3xl font-bold mt-1">{result.formatted.pembulatan}</p>
            <p className="text-sm opacity-90 mt-2 italic">Terbilang: {result.terbilang}</p>
          </div>

          {/* Export */}
          <div className="flex gap-3">
            <button onClick={exportExcel} className="px-3 py-2 rounded-xl bg-green-600 text-white text-sm hover:bg-green-700">
              Export Excel
            </button>
            <button onClick={exportPDF} className="px-3 py-2 rounded-xl bg-red-600 text-white text-sm hover:bg-red-700">
              Export PDF
            </button>
          </div>
        </>
      )}
    </div>
  );
}
